import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CreditCard, Wallet, Shield, AlertCircle, Loader2 } from "lucide-react";
import PaymentItem from "components/payment-item/payment-item";
import API from "../../utils/api";

const PaymentMethodsPage = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.get("/payment-methods");
      if (response.data.payment_methods) {
        setMethods(response.data.payment_methods);
      } else {
        setMethods([]);
      }
    } catch (e) {
      console.error("Failed to fetch payment methods:", e);
      setError("Failed to load payment methods. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // On focus of this tab, fetch the profile
  React.useEffect(() => {
    fetchMethods().then();
  }, []);

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <div className="p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                >
                  <Wallet className="w-8 h-8 text-blue-400" />
                </motion.div>
                Payment Methods
              </h2>
              <p className="text-white/60 mt-2">
                Manage your payment methods and billing preferences
              </p>
            </div>
          </div>
        </motion.div>

        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center gap-3 backdrop-blur-md"
            >
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-300">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          {/* Saved Payment Methods Card */}
          <motion.div
            variants={itemAnimation}
            className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
          >
            {/* Header section - removed since it's redundant with page header */}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin mb-3" />
                <span className="text-white/60">
                  Loading payment methods...
                </span>
              </div>
            ) : methods.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 mb-4">
                  <CreditCard className="w-8 h-8 text-white/60" />
                </div>
                <p className="text-white/80 mb-2">No payment methods saved</p>
                <p className="text-sm text-white/60">
                  You can add payment methods when making a payment.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence>
                  {methods.map((payment, index) => (
                    <motion.div
                      key={payment.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <PaymentItem item={payment} fetchMethods={fetchMethods} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Security Badge */}
            <div className="mt-6 p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-300">
                Your payment information is encrypted and secure
              </span>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PaymentMethodsPage;
